import * as XLSX from 'xlsx';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';

pdfjsLib.GlobalWorkerOptions.workerSrc =
    'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.6.347/pdf.worker.min.js';

// ============================================================
// PDF Text Extraction
// ============================================================
const extractTextFromPDF = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const pages = [];

    for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const items = textContent.items.map(item => ({
            text: item.str,
            x: Math.round(item.transform[4]),
            y: Math.round(item.transform[5]),
            width: item.width,
            height: item.height
        }));
        pages.push(items);
    }
    return pages;
};

// Group text items into rows based on Y coordinate
const groupIntoRows = (items, tolerance = 3) => {
    const sorted = [...items].sort((a, b) => b.y - a.y || a.x - b.x);
    const rows = [];
    let currentRow = [];
    let currentY = null;

    sorted.forEach(item => {
        if (currentY === null || Math.abs(item.y - currentY) <= tolerance) {
            currentRow.push(item);
            if (currentY === null) currentY = item.y;
        } else {
            if (currentRow.length > 0) rows.push(currentRow.sort((a, b) => a.x - b.x));
            currentRow = [item];
            currentY = item.y;
        }
    });
    if (currentRow.length > 0) rows.push(currentRow.sort((a, b) => a.x - b.x));
    return rows;
};

// ============================================================
// Excel Extraction
// ============================================================
const extractDataFromExcel = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'binary', cellDates: true });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const data = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: '' });
                resolve(data);
            } catch (e) {
                reject(e);
            }
        };
        reader.onerror = reject;
        reader.readAsBinaryString(file);
    });
};

// ============================================================
// Date parsing helpers
// ============================================================
const MONTH_MAP = {
    'JAN': 0, 'FEB': 1, 'MAR': 2, 'APR': 3, 'MAY': 4, 'JUN': 5,
    'JUL': 6, 'AUG': 7, 'SEP': 8, 'OCT': 9, 'NOV': 10, 'DEC': 11
};

const parseDate = (dateStr) => {
    if (!dateStr) return null;
    dateStr = dateStr.toString().trim();

    // BKT format: DD-MMM-YY (e.g. 03-JAN-25)
    let match = dateStr.match(/^(\d{1,2})-([A-Z]{3})-(\d{2})$/i);
    if (match) {
        const day = parseInt(match[1]);
        const month = MONTH_MAP[match[2].toUpperCase()];
        let year = parseInt(match[3]);
        year = year < 50 ? 2000 + year : 1900 + year;
        if (month !== undefined) return new Date(year, month, day);
    }

    // DD.MM.YYYY or DD/MM/YYYY (European: day first!)
    match = dateStr.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})$/);
    if (match) return new Date(parseInt(match[3]), parseInt(match[2]) - 1, parseInt(match[1]));

    // DD.MM.YYYY HH:MM:SS (BPB format)
    match = dateStr.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})\s+\d{2}:\d{2}:\d{2}$/);
    if (match) return new Date(parseInt(match[3]), parseInt(match[2]) - 1, parseInt(match[1]));

    // yyyy-MM-dd
    match = dateStr.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (match) return new Date(parseInt(match[1]), parseInt(match[2]) - 1, parseInt(match[3]));

    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? null : d;
};

// Parse amount - handles both standard (1,000.50) and European (1.000,50) formats
// Removes EUR suffix, currency symbols, spaces
const parseAmount = (amountStr) => {
    if (!amountStr && amountStr !== 0) return 0;
    let str = amountStr.toString().trim();
    // Remove currency text and symbols
    str = str.replace(/EUR/gi, '').replace(/[€$£\s]/g, '').trim();
    if (!str) return 0;

    // Determine format:
    // European: 1.000,50 or 5,78 (comma is decimal)
    // Standard: 1,000.50 or 5.78 (dot is decimal)
    if (str.includes(',') && str.includes('.')) {
        if (str.lastIndexOf(',') > str.lastIndexOf('.')) {
            // European: 1.000,50 -> 1000.50
            str = str.replace(/\./g, '').replace(',', '.');
        } else {
            // Standard: 1,000.50 -> 1000.50
            str = str.replace(/,/g, '');
        }
    } else if (str.includes(',')) {
        // Could be European decimal (5,78) or standard thousands (1,000)
        // If comma is followed by exactly 2 digits at end, treat as decimal
        if (/,\d{2}$/.test(str)) {
            str = str.replace(',', '.');
        } else {
            // Thousands separator
            str = str.replace(/,/g, '');
        }
    }

    const num = parseFloat(str);
    return isNaN(num) ? 0 : Math.abs(num);
};

// Parse European amount specifically (comma = decimal, dot = thousands)
// Used for BPB where we know format is European
const parseEuropeanAmount = (amountStr) => {
    if (!amountStr && amountStr !== 0) return 0;
    let str = amountStr.toString().trim();
    str = str.replace(/EUR/gi, '').replace(/[€$£\s]/g, '').trim();
    if (!str) return 0;

    // Always treat as European: dots are thousands, comma is decimal
    str = str.replace(/\./g, '').replace(',', '.');
    const num = parseFloat(str);
    return isNaN(num) ? 0 : Math.abs(num);
};

// Helper: build lines from pages
const buildLines = (pages, tolerance = 3) => {
    const allLines = [];
    pages.forEach((pageItems, pageIdx) => {
        const rows = groupIntoRows(pageItems, tolerance);
        rows.forEach(row => {
            const sortedItems = row.sort((a, b) => a.x - b.x);
            const lineText = sortedItems.map(r => r.text.trim()).filter(t => t).join('  ');
            allLines.push({ text: lineText, items: sortedItems, page: pageIdx });
        });
    });
    return allLines;
};

// Helper: find column X positions from header row
const findColumnPositions = (allLines, tag, debiKeywords, krediKeywords, balanceKeywords) => {
    let debitColX = -1, creditColX = -1, balanceColX = -1;

    for (const line of allLines) {
        const lower = line.text.toLowerCase();
        const hasDebi = debiKeywords.some(k => lower.includes(k));
        const hasKredi = krediKeywords.some(k => lower.includes(k));
        if (hasDebi && hasKredi) {
            line.items.forEach(item => {
                const t = item.text.toLowerCase().trim();
                if (debiKeywords.some(k => t.includes(k))) debitColX = item.x;
                if (krediKeywords.some(k => t.includes(k))) creditColX = item.x;
                if (balanceKeywords.some(k => t.includes(k))) balanceColX = item.x;
            });
            break;
        }
    }

    console.log(`[${tag}] Column positions - Debit: ${debitColX}, Credit: ${creditColX}, Balance: ${balanceColX}`);
    return { debitColX, creditColX, balanceColX };
};

// Helper: assign amounts to debit/credit based on column X positions
// Skips 0.00 values (placeholder for empty columns in bank statements)
const assignAmounts = (amountsWithPos, debitColX, creditColX, balanceColX) => {
    let debit = 0, credit = 0;
    // Filter out 0.00 amounts - they are placeholders for empty columns
    const nonZero = amountsWithPos.filter(a => a.val > 0);

    if (debitColX > 0 && creditColX > 0) {
        nonZero.forEach(a => {
            const distDebit = Math.abs(a.x - debitColX);
            const distCredit = Math.abs(a.x - creditColX);
            const distBalance = balanceColX > 0 ? Math.abs(a.x - balanceColX) : 9999;

            if (distBalance < distDebit && distBalance < distCredit) {
                // balance column - skip
            } else if (distDebit <= distCredit) {
                debit = a.val;
            } else {
                credit = a.val;
            }
        });
    } else {
        // Fallback: last = balance, first/second = debit/credit
        if (nonZero.length >= 3) {
            debit = nonZero[0].val;
            credit = nonZero[1].val;
        } else if (nonZero.length === 2) {
            debit = nonZero[0].val;
        } else if (nonZero.length === 1) {
            if (nonZero[0].neg) {
                credit = nonZero[0].val;
            } else {
                debit = nonZero[0].val;
            }
        }
    }

    return { debit, credit };
};

// Standard amount regex: requires exactly 2 decimal places (avoids matching date fragments)
const STANDARD_AMOUNT_REGEX = /^-?\d{1,3}(?:,\d{3})*\.\d{2}$/;

// ============================================================
// TEB Bank Parser
// ============================================================
// Date: DD/MM/YYYY
// Header: Data | Shpjegimi | Val. | Debi | Kredi | Bilanci | Nr.Trx | Detaje
// Amounts: standard decimal, negative = debi, positive = kredi, 0.00 = empty
// EUR text between description and amounts

const TEB_SKIP_PATTERNS = [
    /transferimi i bilancit/i,
    /page\s+\d+\s*\/\s*\d+/i,
    /278\s*-\s*monitorimi/i,
    /^iban/i,
    /emri tregtar/i,
    /poseduesi/i,
    /teb sh\.?a\.?/i,
    /ankesat/i,
    /complaints/i,
    /prituzbe/i,
    /^data\b.*shpjegimi/i,
    /^data\b.*debi/i,
    /^data\b.*kredi/i,
    /shpjegimi.*val\..*debi/i,
    /debi.*kredi.*bilanci/i,
    /^-{5,}/,
    /^\s*$/,
    /transaksionet p[eë]r periudh/i,
    /monitorimi i transaksioneve/i,
    /monitorimi i bilancit/i,
    /detajet e llogarise/i,
    /detajet e llogarisë/i,
    /^totali\s*eur/i,
    /^totali$/i,
    /fondit p[eë]r sigurimin/i,
    /www\.fsdk/i,
    /qafa city/i,
    /banka je clanica/i,
    /coverage limit/i,
    /sasia\s+\d+/i,
    /nr\.trx/i,
];

const TEB_DATE_REGEX = /(\d{2}\/\d{2}\/\d{4})/;

const parseTEBPDF = (pages) => {
    const transactions = [];
    const allLines = buildLines(pages, 3);

    console.log('[TEB] Total lines extracted:', allLines.length);
    allLines.forEach((line, i) => {
        console.log(`[TEB] Line ${i}: "${line.text}"`);
    });

    // Find column positions from header
    // TEB header: Data | Shpjegimi | Val. | Debi | Kredi | Bilanci | Nr.Trx | Detaje
    let debitColX = -1, creditColX = -1, balanceColX = -1, nrTrxColX = -1;
    for (const line of allLines) {
        const lower = line.text.toLowerCase();
        if (lower.includes('debi') && lower.includes('kredi') && lower.includes('bilanci')) {
            line.items.forEach(item => {
                const t = item.text.toLowerCase().trim();
                if (t === 'debi' || t === 'debit') debitColX = item.x;
                if (t === 'kredi' || t === 'credit') creditColX = item.x;
                if (t.includes('bilanci') || t.includes('balanc')) balanceColX = item.x;
                if (t.includes('nr.trx') || t.includes('nr trx')) nrTrxColX = item.x;
            });
            break;
        }
    }
    console.log('[TEB] Columns - Debi:', debitColX, 'Kredi:', creditColX, 'Bilanci:', balanceColX, 'Nr.Trx:', nrTrxColX);

    let currentTransaction = null;

    allLines.forEach((line) => {
        const lineText = line.text;

        // Skip known patterns
        if (TEB_SKIP_PATTERNS.some(p => p.test(lineText))) return;

        const dateMatch = lineText.match(TEB_DATE_REGEX);

        if (dateMatch) {
            // Save previous
            if (currentTransaction && (currentTransaction.debit > 0 || currentTransaction.credit > 0)) {
                transactions.push(currentTransaction);
            }

            const date = parseDate(dateMatch[1]);
            if (!date) { currentTransaction = null; return; }

            const descParts = [];
            const amountsWithPos = [];
            let reference = '';

            line.items.forEach(item => {
                const val = item.text.trim();
                if (!val) return;

                // Skip date tokens
                if (/^\d{2}\/\d{2}\/\d{4}$/.test(val)) return;
                // Skip EUR
                if (/^EUR$/i.test(val)) return;

                // Check if Nr.Trx (short number near nrTrx column, or standalone 3-5 digit number at right side)
                if (/^\d{2,5}$/.test(val) && (
                    (nrTrxColX > 0 && Math.abs(item.x - nrTrxColX) < 40) ||
                    (item.x > 500 && !reference)
                )) {
                    reference = val;
                    return;
                }

                // Check if amount
                const cleaned = val.replace(/[€$£\s]/g, '');
                const isAmount = STANDARD_AMOUNT_REGEX.test(cleaned);

                if (isAmount) {
                    amountsWithPos.push({ x: item.x, val: parseAmount(val), raw: val, neg: cleaned.startsWith('-') });
                } else {
                    descParts.push(val);
                }
            });

            const description = descParts.join(' ').replace(/\s+/g, ' ').trim();
            amountsWithPos.sort((a, b) => a.x - b.x);

            // TEB: negative = debi, positive = kredi, 0.00 = empty, skip balance
            let debit = 0, credit = 0;
            const nonZero = amountsWithPos.filter(a => a.val > 0);

            if (debitColX > 0 && creditColX > 0 && balanceColX > 0) {
                nonZero.forEach(a => {
                    const distDebit = Math.abs(a.x - debitColX);
                    const distCredit = Math.abs(a.x - creditColX);
                    const distBalance = Math.abs(a.x - balanceColX);

                    if (distBalance < distDebit && distBalance < distCredit) return;

                    if (distDebit <= distCredit) debit = a.val;
                    else credit = a.val;
                });
            } else {
                // Fallback: negative = debi, positive = kredi
                nonZero.forEach(a => {
                    if (a.neg) debit = a.val;
                    else credit = a.val;
                });
            }

            currentTransaction = { date, description, reference, debit, credit };

        } else if (currentTransaction) {
            // Continuation line
            line.items.forEach(item => {
                const val = item.text.trim();
                // Check for Nr.Trx in continuation
                if (/^\d{2,5}$/.test(val) && !currentTransaction.reference &&
                    ((nrTrxColX > 0 && Math.abs(item.x - nrTrxColX) < 40) || item.x > 500)) {
                    currentTransaction.reference = val;
                }
            });

            const extra = line.items
                .map(it => it.text.trim())
                .filter(t => t && t.length > 2 &&
                    !/^\d{2}\/\d{2}\/\d{4}$/.test(t) &&
                    !/^EUR$/i.test(t) &&
                    !/^\d{2,5}$/.test(t) &&
                    !STANDARD_AMOUNT_REGEX.test(t.replace(/[€$£\s]/g, '')))
                .join(' ').trim();

            if (extra && extra.length > 2) {
                currentTransaction.description += ' ' + extra;
            }
        }
    });

    if (currentTransaction && (currentTransaction.debit > 0 || currentTransaction.credit > 0)) {
        transactions.push(currentTransaction);
    }

    transactions.forEach(t => {
        t.description = t.description.replace(/\s+/g, ' ').trim();
    });

    console.log('[TEB] Total transactions found:', transactions.length, transactions);
    return transactions;
};

// ============================================================
// NLB Bank Parser
// ============================================================
// Date: DD.MM.YYYY (two dates per row: transaction date + value date)
// Header: Data.trans | value date | Referenca | pershkrimi | debi | kreditor | Balance
// Reference: numeric codes like 16313353801
// Amounts: standard 3.50, 2,200.00, 0.00

const NLB_SKIP_PATTERNS = [
    /data e shtypjes/i,
    /page\s*no/i,
    /saldoja fillestare/i,
    /saldo fillestare/i,
    /qarkullimi/i,
    /^total/i,
    /nlb banka/i,
    /informacione/i,
    /^iban/i,
    /shuma e mbajtur/i,
    /^period/i,
    /periudha/i,
    /data\.?\s*trans/i,
    /value\s*date.*referenc/i,
    /debi.*kredit/i,
    /kreditor.*balance/i,
    /^-{5,}/,
    /^\s*$/,
    /numri i llogarise/i,
    /numri i llogarisë/i,
    /bilanci hap[eë]s/i,
    /bilanci mbyll[eë]s/i,
    /saldo p[eë]rfundimtare/i,
];

const NLB_DATE_REGEX = /(\d{2}\.\d{2}\.\d{4})/;

const parseNLBPDF = (pages) => {
    const transactions = [];
    const allLines = buildLines(pages, 3);

    console.log('[NLB] Total lines extracted:', allLines.length);
    allLines.forEach((line, i) => {
        const positions = line.items.map(it => `"${it.text}"@x${it.x}`).join(', ');
        console.log(`[NLB] Line ${i}: "${line.text}"`);
        console.log(`[NLB]   Items: ${positions}`);
    });

    // Find column positions
    const cols = findColumnPositions(allLines, 'NLB',
        ['debi'],
        ['kredit', 'kredi'],
        ['balance', 'balanc', 'saldo', 'gjendj']
    );

    let currentTransaction = null;

    allLines.forEach((line) => {
        const lineText = line.text;

        // Skip known patterns
        if (NLB_SKIP_PATTERNS.some(p => p.test(lineText))) return;

        const dateMatch = lineText.match(NLB_DATE_REGEX);

        if (dateMatch) {
            // Extra check: skip meta lines that happen to contain dates
            const lower = lineText.toLowerCase();
            if (lower.includes('data e shtypjes') || lower.includes('periudha') ||
                (lower.includes('saldo') && lower.includes('fillest'))) {
                return;
            }

            // Save previous
            if (currentTransaction && (currentTransaction.debit > 0 || currentTransaction.credit > 0)) {
                transactions.push(currentTransaction);
            }

            const date = parseDate(dateMatch[1]);
            if (!date) { currentTransaction = null; return; }

            const descParts = [];
            const amountsWithPos = [];
            let reference = '';

            line.items.forEach(item => {
                const val = item.text.trim();
                if (!val) return;

                // Skip date tokens (DD.MM.YYYY)
                if (/^\d{2}\.\d{2}\.\d{4}$/.test(val)) return;

                // Check if amount (standard format, exactly 2 decimals)
                const cleaned = val.replace(/[€$£\s]/g, '');
                const isAmount = STANDARD_AMOUNT_REGEX.test(cleaned);

                if (isAmount) {
                    amountsWithPos.push({ x: item.x, val: parseAmount(val), raw: val, neg: val.startsWith('-') });
                } else if (/^\d{8,}$/.test(val)) {
                    // Reference: long numeric code
                    reference = val;
                } else {
                    descParts.push(val);
                }
            });

            const description = descParts.join(' ').replace(/\s+/g, ' ').trim();
            amountsWithPos.sort((a, b) => a.x - b.x);

            console.log('[NLB] Date:', dateMatch[1], 'Ref:', reference, 'Desc:', description,
                'Amounts:', amountsWithPos.map(a => `${a.raw}@x${a.x}`));

            const { debit, credit } = assignAmounts(amountsWithPos, cols.debitColX, cols.creditColX, cols.balanceColX);

            currentTransaction = { date, description, reference, debit, credit };

        } else if (currentTransaction) {
            // Continuation line
            const extra = line.items
                .map(it => it.text.trim())
                .filter(t => t && t.length > 2 &&
                    !/^\d{2}\.\d{2}\.\d{4}$/.test(t) &&
                    !STANDARD_AMOUNT_REGEX.test(t.replace(/[€$£\s]/g, '')) &&
                    !/^[\d,.\\s-]+$/.test(t))
                .join(' ').trim();
            if (extra) currentTransaction.description += ' ' + extra;
        }
    });

    if (currentTransaction && (currentTransaction.debit > 0 || currentTransaction.credit > 0)) {
        transactions.push(currentTransaction);
    }

    transactions.forEach(t => { t.description = t.description.replace(/\s+/g, ' ').trim(); });

    console.log('[NLB] Total transactions found:', transactions.length, transactions);
    return transactions;
};

// ============================================================
// BKT Bank Parser
// ============================================================
// Date: DD-MMM-YY (e.g. 03-JAN-25)
// Header: Data | Pershkrimi | Referenca | Date Valuta | Debi | Kredi | Balanca
// Multi-line with continuation lines (Ft - Event Desc, By Order of, Ben:, Detail:, etc.)
// Reference: 701FIBK250030213, 701PPEB250030116
// Amounts: 119.04, 10.00, 9,900.00, 10,000.00

const BKT_SKIP_PATTERNS = [
    /^opening balance/i,
    /^closing balance/i,
    /page\s*no/i,
    /^accountno/i,
    /^printed on/i,
    /^statement of account/i,
    /^iban\s/i,
    /^bic\/swift/i,
    /^banka kombetare/i,
    /^-{5,}/,
    /debit\(s\)/i,
    /credit\(s\)/i,
    /^fsdk/i,
    /^unauth entry/i,
    /^data krijimit/i,
    /^shenim:/i,
    /^llogaria e-deklarata/i,
    /^from\(nga/i,
    /^date of statement/i,
    /^statement no/i,
    /^date\s+description\s+ref/i,
    /^data\s+pershkrimi\s+ref/i,
    /^bkt-se/i,
    /^depozitues.*siguruar/i,
    /^www\.fsdk/i,
    /^fondit p[eë]r sigurimin/i,
    /^p[eë]r informacione/i,
    /^\s*$/,
];

const BKT_DATE_REGEX = /(\d{1,2}-[A-Z]{3}-\d{2})/i;

const parseBKTPDF = (pages) => {
    const transactions = [];
    // BKT PDFs often have wider Y-spread - use higher tolerance
    const allLines = buildLines(pages, 5);

    console.log('[BKT] Total lines extracted:', allLines.length);

    // Try to find column positions using BOTH character positions and X positions
    let debiCharPos = -1, krediCharPos = -1, balancaCharPos = -1;
    let debiColX = -1, krediColX = -1, balancaColX = -1;
    for (const line of allLines) {
        const lower = line.text.toLowerCase();
        if ((lower.includes('debi') && lower.includes('kredi') && lower.includes('balanc')) ||
            (lower.includes('debit') && lower.includes('credit') && lower.includes('balance'))) {
            // Character positions for monospace fallback
            const debiIdx = lower.indexOf('debi');
            const krediIdx = lower.indexOf('kredi') !== -1 ? lower.indexOf('kredi') : lower.indexOf('credit');
            const balIdx = lower.indexOf('balanc') !== -1 ? lower.indexOf('balanc') : lower.indexOf('balance');
            if (debiIdx >= 0) debiCharPos = debiIdx;
            if (krediIdx >= 0) krediCharPos = krediIdx;
            if (balIdx >= 0) balancaCharPos = balIdx;
            // X positions for PDF coordinate matching
            line.items.forEach(item => {
                const t = item.text.toLowerCase().trim();
                if (t.includes('debi') || t === 'debit') debiColX = item.x;
                if (t.includes('kredi') || t === 'credit') krediColX = item.x;
                if (t.includes('balanc') || t === 'balance') balancaColX = item.x;
            });
            break;
        }
    }
    console.log('[BKT] Char positions - Debi:', debiCharPos, 'Kredi:', krediCharPos, 'Balanca:', balancaCharPos);
    console.log('[BKT] X positions - Debi:', debiColX, 'Kredi:', krediColX, 'Balanca:', balancaColX);

    let currentTransaction = null;

    allLines.forEach((line) => {
        const lineText = line.text;

        // Skip known patterns
        if (BKT_SKIP_PATTERNS.some(p => p.test(lineText))) return;

        const dateMatch = lineText.match(BKT_DATE_REGEX);

        if (dateMatch) {
            const date = parseDate(dateMatch[1]);
            if (!date) return;

            // Save previous transaction
            if (currentTransaction && (currentTransaction.debit > 0 || currentTransaction.credit > 0)) {
                transactions.push(currentTransaction);
            }

            // BKT PDF is monospace text - all items on one line at same X
            // Parse amounts from the line text using regex and character position
            const fullLine = lineText;

            // Remove date tokens from text for description
            let textWithoutDates = fullLine.replace(/\d{1,2}-[A-Z]{3}-\d{2}/gi, '');

            // Find reference (alphanumeric code) - BKT refs: 701FIBK250030213, 701PPEB250030116, FT25069QRH7C, etc.
            const refMatch = textWithoutDates.match(/(\d{3}[A-Z]{2,5}\d{6,}[A-Z0-9]*)/i) ||
                             textWithoutDates.match(/(FT[L]?\d{4,}[A-Z0-9]*)/i) ||
                             textWithoutDates.match(/([A-Z]{2,}\d{8,}[A-Z0-9]*)/i);
            const reference = refMatch ? refMatch[1] : '';

            // Find all amounts with their CHARACTER position in the line
            const amountRegex = /(\d{1,3}(?:,\d{3})*\.\d{2})/g;
            const amounts = [];
            let m;
            while ((m = amountRegex.exec(fullLine)) !== null) {
                amounts.push({ val: parseAmount(m[1]), raw: m[1], charPos: m.index });
            }
            const nonZero = amounts.filter(a => a.val > 0);

            // Build description: remove dates, reference, amounts
            let description = textWithoutDates;
            if (reference) description = description.replace(reference, '');
            description = description.replace(/\d{1,3}(?:,\d{3})*\.\d{2}/g, '');
            description = description.replace(/\s{2,}/g, ' ').replace(/^\s*[|,]\s*/, '').replace(/\s*[|,]\s*$/, '').trim();

            // Assign debit/credit - try X positions first, then char positions, then keyword fallback
            let debit = 0, credit = 0;

            if (debiColX > 0 && krediColX > 0 && nonZero.length > 0) {
                // Strategy 1: Use PDF X coordinates from items
                line.items.forEach(item => {
                    const cleaned = item.text.trim().replace(/[€$£\s]/g, '');
                    if (!STANDARD_AMOUNT_REGEX.test(cleaned)) return;
                    const val = parseAmount(item.text);
                    if (val <= 0) return;
                    const distDebi = Math.abs(item.x - debiColX);
                    const distKredi = Math.abs(item.x - krediColX);
                    const distBal = balancaColX > 0 ? Math.abs(item.x - balancaColX) : 9999;
                    if (distBal < distDebi && distBal < distKredi) return;
                    if (distDebi <= distKredi) debit = val;
                    else credit = val;
                });
            } else if (debiCharPos > 0 && krediCharPos > 0 && balancaCharPos > 0 && nonZero.length > 0) {
                // Strategy 2: Use character positions (monospace PDFs)
                nonZero.forEach(a => {
                    const distDebi = Math.abs(a.charPos - debiCharPos);
                    const distKredi = Math.abs(a.charPos - krediCharPos);
                    const distBalanca = Math.abs(a.charPos - balancaCharPos);
                    if (distBalanca < distDebi && distBalanca < distKredi) return;
                    if (distDebi <= distKredi) debit = a.val;
                    else credit = a.val;
                });
            } else if (nonZero.length > 0) {
                // Strategy 3: Keyword-based fallback
                const upperDesc = description.toUpperCase();
                const isCreditType = upperDesc.includes('CASH DEPOSIT') || upperDesc.includes('ATM CASH DEPOSIT') ||
                    upperDesc.includes('INC ACH') || upperDesc.includes('INC TRF') ||
                    upperDesc.includes('INC SWIFT') || upperDesc.includes('SALARY') ||
                    upperDesc.includes('INCOMING') || upperDesc.includes('CREDIT');
                const isDebitType = upperDesc.includes('PURCHASE') || upperDesc.includes('WITHDRAWAL') ||
                    upperDesc.includes('UTILITY PAYMENT') || upperDesc.includes('COMMISSION') ||
                    upperDesc.includes('CHARGES') || upperDesc.includes('OUT ACH') ||
                    upperDesc.includes('OUT SWIFT') || upperDesc.includes('OUT TRF') ||
                    upperDesc.includes('KOMISION') || upperDesc.includes('CASH WITHDRAWAL') ||
                    upperDesc.includes('PAYMENT') || upperDesc.includes('DEBIT');

                if (nonZero.length >= 2) {
                    // If 2+ amounts (excluding balance), first=debit, second=credit typically
                    debit = nonZero[0].val;
                    credit = nonZero[1].val;
                } else {
                    if (isCreditType) credit = nonZero[0].val;
                    else if (isDebitType) debit = nonZero[0].val;
                    else debit = nonZero[0].val;
                }
            }

            console.log('[BKT] Parsed:', dateMatch[1], 'D:', debit, 'K:', credit, 'Ref:', reference, 'Desc:', description.substring(0, 50));

            currentTransaction = { date, description, reference, debit, credit };

        } else if (currentTransaction) {
            // Continuation line - append useful info
            const skipContinuation = /^\d{6}\*{6}\d{4}/.test(lineText.trim()) ||
                /^QZ[Z]?\s/.test(lineText.trim()) ||
                /^\d{2}:\d{2}:\d{2}/.test(lineText.trim()) ||
                /^60\d{10,}/.test(lineText.trim()) ||
                lineText.trim().length < 3;

            if (!skipContinuation) {
                let extra = lineText
                    .replace(/\d{6}\*{6}\d{4}/g, '')       // card numbers
                    .replace(/\d{1,2}-[A-Z]{3}-\d{2}/gi, '') // dates
                    .replace(/\d{2}:\d{2}:\d{2}\s*(AM|PM)?/gi, '') // timestamps
                    .replace(/QZ[Z]?\s+\d+/g, '')            // QZ codes
                    .replace(/60\d{10,}/g, '')                // transaction codes
                    .replace(/\b\d{1,3}(?:,\d{3})*\.\d{2}\b/g, '') // amounts
                    .replace(/\s{2,}/g, ' ')
                    .trim();

                if (extra && extra.length > 2 && extra !== 'AM' && extra !== 'PM') {
                    currentTransaction.description += ' ' + extra;
                }
            }
        }
    });

    // Push last transaction
    if (currentTransaction && (currentTransaction.debit > 0 || currentTransaction.credit > 0)) {
        transactions.push(currentTransaction);
    }

    // Clean descriptions
    transactions.forEach(t => {
        t.description = t.description
            .replace(/\s+/g, ' ')
            .replace(/By Order of:/g, '| ')
            .replace(/Ben:/g, '| ')
            .replace(/Detail:/g, '| ')
            .trim();
    });

    console.log('[BKT] Total transactions found:', transactions.length, transactions);
    return transactions;
};

// ============================================================
// BPB (Banka per Biznes) Parser
// ============================================================
// Date: DD.MM.YYYY HH:MM:SS (e.g. 28.03.2026 07:34:10)
// Header: Nr. Rendor | Data | Perfituesi | Pershkrimi i transaksionit | Lloji i pageses | Numri i transaksionit | Debi | Kredi | Total
// EUROPEAN number format: comma as decimal (5,78 EUR, 1.500,00 EUR, 2.149,00 EUR)
// Amounts have "EUR" suffix

const BPB_SKIP_PATTERNS = [
    /banka p[eë]r biznes/i,
    /faqja\s+\d+\s*\/\s*\d+/i,
    /bilanci i hapjes/i,
    /^fsdk/i,
    /limiti/i,
    /nr\.?\s*rendor/i,
    /data.*p[eë]rfituesi/i,
    /p[eë]rshkrimi i transaksionit/i,
    /lloji i pages/i,
    /debi.*kredi.*total/i,
    /^-{5,}/,
    /^\s*$/,
    /informacione/i,
];

// BPB date: DD.MM.YYYY HH:MM:SS
const BPB_DATE_REGEX = /(\d{2}\.\d{2}\.\d{4}\s+\d{2}:\d{2}:\d{2})/;
// Also match just DD.MM.YYYY if timestamp is separate
const BPB_DATE_SIMPLE_REGEX = /(\d{2}\.\d{2}\.\d{4})/;

// European amount: 5,78 or 173,10 or 1.500,00 or 2.149,00 (optionally followed by EUR)
const EUROPEAN_AMOUNT_REGEX = /^-?\d{1,3}(?:\.\d{3})*,\d{2}$/;

const parseBPBPDF = (pages) => {
    const transactions = [];
    const allLines = buildLines(pages, 4);

    console.log('[BPB] Total lines extracted:', allLines.length);
    allLines.forEach((line, i) => {
        const positions = line.items.map(it => `"${it.text}"@x${it.x}`).join(', ');
        console.log(`[BPB] Line ${i}: "${line.text}"`);
        console.log(`[BPB]   Items: ${positions}`);
    });

    // Find column positions
    let debitColX = -1, creditColX = -1, totalColX = -1;
    for (const line of allLines) {
        const lower = line.text.toLowerCase();
        if (lower.includes('debi') && lower.includes('kredi')) {
            line.items.forEach(item => {
                const t = item.text.toLowerCase().trim();
                if (t.includes('debi')) debitColX = item.x;
                if (t.includes('kredi')) creditColX = item.x;
                if (t === 'total') totalColX = item.x;
            });
            break;
        }
    }
    console.log(`[BPB] Column positions - Debit: ${debitColX}, Credit: ${creditColX}, Total: ${totalColX}`);

    let currentTransaction = null;

    allLines.forEach((line) => {
        const lineText = line.text;

        // Skip known patterns
        if (BPB_SKIP_PATTERNS.some(p => p.test(lineText))) return;

        // Check for date (DD.MM.YYYY HH:MM:SS or DD.MM.YYYY)
        const fullDateMatch = lineText.match(BPB_DATE_REGEX);
        const simpleDateMatch = lineText.match(BPB_DATE_SIMPLE_REGEX);
        const dateMatch = fullDateMatch || simpleDateMatch;

        if (dateMatch) {
            // Verify it's actually a transaction line (has a date at the start area)
            // Check the dateMatch isn't in a skip line
            const lower = lineText.toLowerCase();
            if (lower.includes('bilanci') || lower.includes('faqja') || lower.includes('limiti')) return;

            // Save previous
            if (currentTransaction && (currentTransaction.debit > 0 || currentTransaction.credit > 0)) {
                transactions.push(currentTransaction);
            }

            const date = parseDate(dateMatch[1]);
            if (!date) { currentTransaction = null; return; }

            const descParts = [];
            const amountsWithPos = [];
            let reference = '';

            line.items.forEach(item => {
                const val = item.text.trim();
                if (!val) return;

                // Skip date tokens
                if (/^\d{2}\.\d{2}\.\d{4}$/.test(val)) return;
                if (/^\d{2}:\d{2}:\d{2}$/.test(val)) return;
                // Skip EUR suffix
                if (/^EUR$/i.test(val)) return;
                // Skip row numbers (Nr. Rendor)
                if (/^\d{1,4}$/.test(val) && item.x < 50) return;

                // Check if European amount (comma decimal)
                // BPB amounts: "5,78 EUR", "5,78", "1.500,00 EUR", "1.500,00", "173,10 EUR"
                const cleanedForEuro = val.replace(/\s*EUR\s*/gi, '').trim();
                const isEuroAmount = EUROPEAN_AMOUNT_REGEX.test(cleanedForEuro);
                // Also check for amounts without EUR that have comma (not reference numbers)
                const isSimpleEuro = /^\d{1,3}(?:\.\d{3})*,\d{2}$/.test(cleanedForEuro);

                if (isEuroAmount || isSimpleEuro) {
                    const parsed = parseEuropeanAmount(cleanedForEuro);
                    if (parsed >= 0) {
                        amountsWithPos.push({
                            x: item.x,
                            val: parsed,
                            raw: val,
                            neg: cleanedForEuro.startsWith('-')
                        });
                    }
                } else if (/^\d{10,}$/.test(val) || /^[A-Z]\d{10,}$/.test(val) || /^IF\d+$/.test(val) || /^\d{14}$/.test(val)) {
                    // Transaction number / reference (99542979637009, K2615864410001, IF000015665011)
                    reference = val;
                } else if (/^\d{5,9}$/.test(val) && item.x > 400) {
                    // Reference numbers in right area
                    if (!reference) reference = val;
                    else reference += val;
                } else {
                    descParts.push(val);
                }
            });

            const description = descParts.join(' ').replace(/\s+/g, ' ').trim();
            amountsWithPos.sort((a, b) => a.x - b.x);

            console.log('[BPB] Date:', dateMatch[1], 'Ref:', reference, 'Desc:', description,
                'Amounts:', amountsWithPos.map(a => `${a.raw}(${a.val})@x${a.x}`));

            // BPB: each row has Debi OR Kredi (one amount), plus Total (balance)
            // Strategy: BPB has exactly 1 real amount per row + 1 balance (Total)
            // The real amount is in Debi or Kredi column, Total is always rightmost
            let debit = 0, credit = 0;
            const nonZeroAmounts = amountsWithPos.filter(a => a.val > 0);

            console.log('[BPB] NonZero amounts:', nonZeroAmounts.map(a => `${a.val}@x${a.x}`),
                'Cols: D=', debitColX, 'K=', creditColX, 'T=', totalColX);

            if (nonZeroAmounts.length >= 2 && totalColX > 0) {
                // Multiple amounts - find the one closest to Debi or Kredi col, skip Total
                nonZeroAmounts.forEach(a => {
                    const distDebit = debitColX > 0 ? Math.abs(a.x - debitColX) : 9999;
                    const distCredit = creditColX > 0 ? Math.abs(a.x - creditColX) : 9999;
                    const distTotal = Math.abs(a.x - totalColX);

                    console.log('[BPB]   Amount', a.val, '@x', a.x, 'distD=', distDebit, 'distK=', distCredit, 'distT=', distTotal);

                    // If closest to Total - this is balance, skip
                    if (distTotal < distDebit && distTotal < distCredit) return;

                    // Assign to closest column
                    if (distDebit < distCredit) {
                        debit = a.val;
                    } else {
                        credit = a.val;
                    }
                });
            } else if (nonZeroAmounts.length === 1) {
                // Only one non-zero amount - it's the transaction amount (Total is 0 or same)
                // Need to determine if it's debi or kredi
                const a = nonZeroAmounts[0];
                if (debitColX > 0 && creditColX > 0) {
                    const distDebit = Math.abs(a.x - debitColX);
                    const distCredit = Math.abs(a.x - creditColX);
                    if (distDebit < distCredit) debit = a.val;
                    else credit = a.val;
                } else {
                    debit = a.val;
                }
            }

            currentTransaction = { date, description, reference, debit, credit };

        } else if (currentTransaction) {
            // Continuation line - also look for amounts here (BPB sometimes splits amounts to next line)
            let foundAmount = false;
            line.items.forEach(item => {
                const val = item.text.trim();
                if (!val) return;
                const cleanedForEuro = val.replace(/\s*EUR\s*/gi, '').trim();
                const isEuro = EUROPEAN_AMOUNT_REGEX.test(cleanedForEuro) || /^\d{1,3}(?:\.\d{3})*,\d{2}$/.test(cleanedForEuro);
                if (isEuro) {
                    const parsed = parseEuropeanAmount(cleanedForEuro);
                    if (parsed > 0 && currentTransaction.debit === 0 && currentTransaction.credit === 0) {
                        // Assign to debit or credit based on column positions
                        if (debitColX > 0 && creditColX > 0) {
                            const distDebit = Math.abs(item.x - debitColX);
                            const distCredit = Math.abs(item.x - creditColX);
                            const distTotal = totalColX > 0 ? Math.abs(item.x - totalColX) : 9999;
                            if (distTotal < distDebit && distTotal < distCredit) return;
                            if (totalColX > 0 && distTotal < 30) return;
                            if (distDebit < distCredit) currentTransaction.debit = parsed;
                            else if (distCredit < distDebit) currentTransaction.credit = parsed;
                        }
                        foundAmount = true;
                    }
                }
            });

            // Also check for reference in continuation lines
            if (!currentTransaction.reference) {
                line.items.forEach(item => {
                    const val = item.text.trim();
                    if (/^\d{10,}$/.test(val) || /^[A-Z]\d{10,}$/.test(val) || /^IF\d+$/.test(val)) {
                        currentTransaction.reference = val;
                    }
                });
            }

            if (!foundAmount) {
                const extra = line.items
                    .map(it => it.text.trim())
                    .filter(t => t && t.length > 2 &&
                        !/^\d{2}\.\d{2}\.\d{4}$/.test(t) &&
                        !/^\d{2}:\d{2}:\d{2}$/.test(t) &&
                        !/^EUR$/i.test(t) &&
                        !EUROPEAN_AMOUNT_REGEX.test(t.replace(/\s*EUR\s*/gi, '').trim()) &&
                        !/^\d{1,3}(?:\.\d{3})*,\d{2}$/.test(t.replace(/\s*EUR\s*/gi, '').trim()))
                    .join(' ').trim();
                if (extra) currentTransaction.description += ' ' + extra;
            }
        }
    });

    // Push last
    if (currentTransaction && (currentTransaction.debit > 0 || currentTransaction.credit > 0)) {
        transactions.push(currentTransaction);
    }

    transactions.forEach(t => { t.description = t.description.replace(/\s+/g, ' ').trim(); });

    console.log('[BPB] Total transactions found:', transactions.length, transactions);
    return transactions;
};

// ============================================================
// Generic Excel parser (used by all banks for Excel files)
// ============================================================
const parseGenericBankExcel = (data) => {
    const transactions = [];
    let startRow = -1;
    let colMap = {};

    // Auto-detect header row
    for (let i = 0; i < Math.min(data.length, 20); i++) {
        const row = data[i].map(c => (c || '').toString().toLowerCase().trim());
        const hasDate = row.findIndex(c => c.includes('dat') || c === 'date' || c === 'data');
        const hasAmount = row.findIndex(c =>
            c.includes('debi') || c.includes('debit') || c.includes('kredi') || c.includes('credit') ||
            c.includes('shum') || c.includes('amount') || c.includes('hyr') || c.includes('dal')
        );

        if (hasDate >= 0 && hasAmount >= 0) {
            console.log('[Excel Parser] Header found at row', i, ':', row);
            row.forEach((cell, idx) => {
                if ((cell.includes('dat') || cell === 'date' || cell === 'data') && colMap.date === undefined) colMap.date = idx;
                if (cell.includes('pershkrim') || cell.includes('përshkrim') || cell.includes('desc') || cell.includes('detaj') || cell.includes('shpjegim') || cell.includes('perfitues') || cell.includes('përfitues')) colMap.description = idx;
                if (cell.includes('debit') || cell.includes('debi') || cell.includes('terheqje') || cell.includes('dalje') || cell.includes('dal')) colMap.debit = idx;
                if (cell.includes('kredit') || cell.includes('credit') || cell.includes('kredi') || cell.includes('depozit') || cell.includes('hyrje') || cell.includes('hyr')) colMap.credit = idx;
                if (cell.includes('shum') || cell.includes('amount')) colMap.amount = idx;
                if (cell.includes('referenc') || cell.includes('numri i transaksionit') || cell.includes('id e transaksionit') || cell.includes('id transaksionit') || cell.includes('numri i') || cell.includes('nr.trx') || cell.includes('nr trx') || (cell.includes('ref') && !cell.includes('pershkrim') && !cell.includes('përshkrim'))) colMap.reference = idx;
            });
            console.log('[Excel Parser] Column map:', colMap);
            startRow = i + 1;
            break;
        }
    }

    // If no header found, try to find first row with a date
    if (startRow === -1) {
        for (let i = 0; i < Math.min(data.length, 20); i++) {
            const row = data[i];
            if (row && row.length >= 3) {
                for (let j = 0; j < row.length; j++) {
                    if (parseDate(row[j])) {
                        colMap.date = j;
                        colMap.description = j + 1;
                        colMap.debit = j + 2;
                        colMap.credit = j + 3;
                        startRow = i;
                        break;
                    }
                }
                if (startRow >= 0) break;
            }
        }
    }

    if (startRow === -1) return transactions;

    for (let i = startRow; i < data.length; i++) {
        const row = data[i];
        if (!row || row.length < 2) continue;

        const dateVal = parseDate(row[colMap.date !== undefined ? colMap.date : 0]);
        if (!dateVal) continue;

        const description = colMap.description !== undefined ? (row[colMap.description] || '').toString().trim() : '';
        // Reference: always treat as string, never parse as number
        let reference = '';
        if (colMap.reference !== undefined && row[colMap.reference] !== undefined && row[colMap.reference] !== null) {
            reference = row[colMap.reference].toString().trim();
        }

        // Skip opening/closing balance
        if (/opening balance/i.test(description) || /closing balance/i.test(description)) continue;

        let debit = 0, credit = 0;
        if (colMap.debit !== undefined && colMap.credit !== undefined) {
            debit = parseAmount(row[colMap.debit]);
            credit = parseAmount(row[colMap.credit]);
        } else if (colMap.amount !== undefined) {
            const amountStr = (row[colMap.amount] || '').toString().trim();
            const amount = parseAmount(amountStr);
            if (amountStr.startsWith('-')) credit = amount;
            else debit = amount;
        }

        if (debit > 0 || credit > 0) {
            transactions.push({ date: dateVal, description, reference, debit, credit });
        }
    }
    return transactions;
};

// ============================================================
// Generic PDF parser (fallback)
// ============================================================
const parseGenericBankPDF = (pages) => {
    const transactions = [];
    const allLines = buildLines(pages, 3);

    const skipPatterns = [
        /data e shtypjes/i, /saldoja fillestare/i, /saldo fillestare/i,
        /bilanci hap[eë]s/i, /bilanci mbyll[eë]s/i, /opening balance/i, /closing balance/i,
        /saldo p[eë]rfundimtare/i, /^period/i, /periudha/i, /^totali/i, /^total /i,
        /^IBAN/i, /^faqe/i, /^page /i, /^-{5,}/, /llogaria/i,
    ];

    // Detect columns
    const cols = findColumnPositions(allLines, 'GENERIC',
        ['debi', 'debit', 'terheq', 'borxh', 'ngarkes'],
        ['kredi', 'kredit', 'credit', 'dobi'],
        ['balanc', 'saldo', 'gjendj', 'bilanci']
    );

    const DATE_REGEX = /(\d{1,2}[./]\d{1,2}[./]\d{4})/;
    const BKT_DATE = /(\d{1,2}-[A-Z]{3}-\d{2})/i;
    let currentTransaction = null;

    allLines.forEach(line => {
        const lineText = line.text;
        if (!lineText.trim()) return;
        if (skipPatterns.some(p => p.test(lineText))) return;

        const dateMatch = lineText.match(DATE_REGEX) || lineText.match(BKT_DATE);

        if (dateMatch) {
            const lower = lineText.toLowerCase();
            if (lower.includes('data e shtypjes') || (lower.includes('saldo') && lower.includes('fillest'))) return;

            if (currentTransaction && (currentTransaction.debit > 0 || currentTransaction.credit > 0)) {
                transactions.push(currentTransaction);
            }

            const date = parseDate(dateMatch[1]);
            if (!date) { currentTransaction = null; return; }

            const descParts = [];
            const amountsWithPos = [];

            line.items.forEach(item => {
                const val = item.text.trim();
                if (!val) return;
                if (/^\d{1,2}[./]\d{1,2}[./]\d{4}$/.test(val)) return;
                if (/^\d{1,2}-[A-Z]{3}-\d{2}$/i.test(val)) return;

                const cleaned = val.replace(/[€$£\s]/g, '');
                const isAmount = STANDARD_AMOUNT_REGEX.test(cleaned);

                if (isAmount && parseAmount(val) > 0) {
                    amountsWithPos.push({ x: item.x, val: parseAmount(val), neg: val.startsWith('-') });
                } else {
                    descParts.push(val);
                }
            });

            const description = descParts.join(' ').replace(/\s+/g, ' ').trim();
            amountsWithPos.sort((a, b) => a.x - b.x);

            const { debit, credit } = assignAmounts(amountsWithPos, cols.debitColX, cols.creditColX, cols.balanceColX);

            currentTransaction = { date, description, reference: '', debit, credit };

        } else if (currentTransaction) {
            const extra = line.items
                .map(it => it.text.trim())
                .filter(t => t && t.length > 2 &&
                    !/^\d{1,2}[./]\d{1,2}[./]\d{4}$/.test(t) &&
                    !STANDARD_AMOUNT_REGEX.test(t.replace(/[€$£\s]/g, '')) &&
                    !/^[\d,.\\s-]+$/.test(t))
                .join(' ').trim();
            if (extra) currentTransaction.description += ' ' + extra;
        }
    });

    if (currentTransaction && (currentTransaction.debit > 0 || currentTransaction.credit > 0)) {
        transactions.push(currentTransaction);
    }

    transactions.forEach(t => { t.description = t.description.replace(/\s+/g, ' ').trim(); });
    return transactions;
};

// ============================================================
// Bank configurations
// ============================================================
const bkt = {
    label: 'BKT',
    fullLabel: 'BKT – Banka Kombëtare Tregtare',
    parseExcel: parseGenericBankExcel,
    parsePDF: parseBKTPDF
};

const teb = {
    label: 'TEB',
    fullLabel: 'TEB Bank',
    parseExcel: parseGenericBankExcel,
    parsePDF: parseTEBPDF
};

const nlb = {
    label: 'NLB',
    fullLabel: 'NLB Bank',
    parseExcel: parseGenericBankExcel,
    parsePDF: parseNLBPDF
};

const bpb = {
    label: 'BPB',
    fullLabel: 'BPB – Banka për Biznes',
    parseExcel: parseGenericBankExcel,
    parsePDF: parseBPBPDF
};

// --- Raiffeisen Bank ---
// Date: DD.MM.YYYY
// Header: Data / Date | Pershkrimi / Description | Referenca / Ref. | Debi / Debit | Kredi / Credit | Bilanci / Bal.
// Reference: FT240830C1GQ, FTL24086VZ91M
// Multi-line descriptions (POS TRANSACTION, card number, auth code)

const RBKO_SKIP_PATTERNS = [
    /raiffeisen/i,
    /member of rbi/i,
    /gjendja e llogarise/i,
    /account statement/i,
    /te dhenat e klientit/i,
    /client details/i,
    /gjendja fillestare/i,
    /total debi/i,
    /total kredi/i,
    /gjendja p[eë]rmbyll/i,
    /periudha.*period/i,
    /l[eë]shuar m[eë]/i,
    /^\d+\s*\/\s*\d+$/,  // page numbers like "1 / 36"
    /^data.*date.*p[eë]rshkrimi/i,
    /debi.*debit.*kredi.*credit/i,
    /^emri/i,
    /^id e klientit/i,
    /^xhirollogaria/i,
    /^iban/i,
    /^monedha/i,
    /^kategoria/i,
    /fsdk/i,
    /www\.fsdk/i,
    /^\s*$/,
];

const parseRBKOPDF = (pages) => {
    const transactions = [];
    const allLines = buildLines(pages, 3);

    console.log('[RBKO] Total lines:', allLines.length);

    // Find column positions
    const cols = findColumnPositions(allLines, 'RBKO',
        ['debi'],
        ['kredi'],
        ['bilanci', 'bal']
    );

    // Also find reference column
    let refColX = -1;
    for (const line of allLines) {
        const lower = line.text.toLowerCase();
        if (lower.includes('referenc') && lower.includes('debi')) {
            line.items.forEach(item => {
                const t = item.text.toLowerCase().trim();
                if (t.includes('referenc') || t.includes('ref')) refColX = item.x;
            });
            break;
        }
    }
    console.log('[RBKO] Ref col:', refColX);

    let currentTransaction = null;
    const DATE_REGEX = /(\d{2}\.\d{2}\.\d{4})/;

    allLines.forEach(line => {
        const lineText = line.text;
        if (RBKO_SKIP_PATTERNS.some(p => p.test(lineText))) return;

        const dateMatch = lineText.match(DATE_REGEX);

        if (dateMatch) {
            // Skip meta lines with dates
            const lower = lineText.toLowerCase();
            if (lower.includes('gjendja') || lower.includes('periudha') || lower.includes('l\u00ebshuar')) return;

            if (currentTransaction && (currentTransaction.debit > 0 || currentTransaction.credit > 0)) {
                transactions.push(currentTransaction);
            }

            const date = parseDate(dateMatch[1]);
            if (!date) { currentTransaction = null; return; }

            const descParts = [];
            const amountsWithPos = [];
            let reference = '';

            line.items.forEach(item => {
                const val = item.text.trim();
                if (!val) return;
                if (/^\d{2}\.\d{2}\.\d{4}$/.test(val)) return;

                // Reference: FT..., FTL...
                if (/^FT[L]?\d{4,}/i.test(val)) {
                    reference = val;
                    return;
                }

                const cleaned = val.replace(/[€$£\s]/g, '');
                if (STANDARD_AMOUNT_REGEX.test(cleaned) && parseAmount(val) > 0) {
                    amountsWithPos.push({ x: item.x, val: parseAmount(val), neg: cleaned.startsWith('-') });
                } else {
                    descParts.push(val);
                }
            });

            const description = descParts.join(' ').replace(/\s+/g, ' ').trim();
            amountsWithPos.sort((a, b) => a.x - b.x);

            const { debit, credit } = assignAmounts(amountsWithPos, cols.debitColX, cols.creditColX, cols.balanceColX);

            currentTransaction = { date, description, reference, debit, credit };

        } else if (currentTransaction) {
            // Continuation - check for reference
            line.items.forEach(item => {
                const val = item.text.trim();
                if (/^FT[L]?\d{4,}/i.test(val) && !currentTransaction.reference) {
                    currentTransaction.reference = val;
                }
            });

            // Add useful description parts
            const extra = line.items
                .map(it => it.text.trim())
                .filter(t => t && t.length > 2 &&
                    !/^\d{2}\.\d{2}\.\d{4}$/.test(t) &&
                    !/^FT[L]?\d{4,}/i.test(t) &&
                    !STANDARD_AMOUNT_REGEX.test(t.replace(/[€$£\s]/g, '')) &&
                    !/^\d{6}\*{6}\d{4}$/.test(t) &&  // card numbers
                    !/^Auth Code/i.test(t) &&
                    !/^Card number/i.test(t) &&
                    !/^Original time/i.test(t))
                .join(' ').trim();

            if (extra && extra.length > 2) {
                currentTransaction.description += ' ' + extra;
            }
        }
    });

    if (currentTransaction && (currentTransaction.debit > 0 || currentTransaction.credit > 0)) {
        transactions.push(currentTransaction);
    }

    transactions.forEach(t => { t.description = t.description.replace(/\s+/g, ' ').trim(); });

    console.log('[RBKO] Total transactions:', transactions.length);
    return transactions;
};

const rbko = {
    label: 'RBKO',
    fullLabel: 'Raiffeisen Bank Kosova',
    parseExcel: parseGenericBankExcel,
    parsePDF: parseRBKOPDF
};

// ============================================================
// Banka Ekonomike (BEK) Parser
// ============================================================
// Date: DD.MM.YYYY (two dates per row: Data Oper. + Data)
// Header: Data Oper. | Data | Përshkrimi | Vlera (EUR) | Bilanci
// EUROPEAN number format: comma as decimal, dot as thousands
// Single amount column "Vlera (EUR)" with sign: negative = debit, positive = credit
// Multi-line descriptions with RIF. NO. references
// Balance column "Bilanci"

const BEK_SKIP_PATTERNS = [
    /banka\s+ekonomike/i,
    /lista e regjistrimeve/i,
    /kontrata\s+\d+/i,
    /shfryt[eë]zuesi/i,
    /e printuar m[eë]/i,
    /pozicioni/i,
    /^iban/i,
    /periudha/i,
    /l[eë]vizjet e fundit/i,
    /data\s+oper\.\s+data\s+p[eë]rshkrimi/i,
    /vlera\s*\(eur\)/i,
    /regjistrimet:/i,
    /p[eë]r m[eë] shum[eë] informata/i,
    /call center/i,
    /e-mail:\s*info/i,
    /^\d+\s+nga\s+\d+$/i,
    /^\s*$/,
    /bilanci fillestar/i,
    /total kredi/i,
    /total debit/i,
    /bilanci p[eë]rfundimtar/i,
];

const parseBEKPDF = (pages) => {
    const transactions = [];
    const allLines = buildLines(pages, 3);

    console.log('[BEK] Total lines extracted:', allLines.length);

    let currentTransaction = null;

    allLines.forEach((line) => {
        const lineText = line.text;

        // Skip known patterns
        if (BEK_SKIP_PATTERNS.some(p => p.test(lineText))) return;

        // Check for transaction line: starts with two dates DD.MM.YYYY DD.MM.YYYY
        const twoDateMatch = lineText.match(/^(\d{2}\.\d{2}\.\d{4})\s+(\d{2}\.\d{2}\.\d{4})\s+(.+)/);

        if (twoDateMatch) {
            // Save previous transaction
            if (currentTransaction && (currentTransaction.debit > 0 || currentTransaction.credit > 0)) {
                transactions.push(currentTransaction);
            }

            const date = parseDate(twoDateMatch[1]); // Data Oper.
            if (!date) { currentTransaction = null; return; }

            // Extract European amounts from line items
            // BEK amounts: -39,00 / 413,10 / -2,99 / 1.835,80 / -201,00 / 1.434,80
            const amountsWithPos = [];
            const descParts = [];

            line.items.forEach(item => {
                const val = item.text.trim();
                if (!val) return;

                // Skip date tokens
                if (/^\d{2}\.\d{2}\.\d{4}$/.test(val)) return;

                // Check for European amount (with optional negative sign)
                const cleanVal = val.replace(/\s/g, '');
                if (/^-?\d{1,3}(?:\.\d{3})*,\d{2}$/.test(cleanVal)) {
                    const isNeg = cleanVal.startsWith('-');
                    const parsed = parseEuropeanAmount(cleanVal);
                    amountsWithPos.push({ x: item.x, val: parsed, raw: val, neg: isNeg });
                } else {
                    descParts.push(val);
                }
            });

            const description = descParts.join(' ').replace(/\s+/g, ' ').trim();
            amountsWithPos.sort((a, b) => a.x - b.x);

            // BEK has 2 amounts per row: Vlera (transaction) and Bilanci (balance)
            // Vlera is first (leftmost), Bilanci is second (rightmost)
            // Vlera negative = debit, Vlera positive = credit
            let debit = 0, credit = 0;

            if (amountsWithPos.length >= 1) {
                // First amount is Vlera (transaction amount)
                const vlera = amountsWithPos[0];
                if (vlera.neg) {
                    debit = vlera.val;
                } else {
                    credit = vlera.val;
                }
            }

            currentTransaction = { date, description, reference: '', debit, credit };

        } else if (currentTransaction) {
            // Continuation line - extract reference and additional description
            const lineTextTrimmed = lineText.trim();

            // Check for RIF. NO. reference
            const rifMatch = lineTextTrimmed.match(/RIF\.\s*NO\.\s*(\d+)/i);
            if (rifMatch) {
                currentTransaction.reference = rifMatch[1];
                return; // Don't add RIF. NO. line to description
            }

            // Skip page number lines like "1 Nga 161"
            if (/^\.\s*\d+\s+nga\s+\d+/i.test(lineTextTrimmed)) return;
            if (/^\d+\s+nga\s+\d+/i.test(lineTextTrimmed)) return;

            // Skip standalone EUR amount lines (e.g. "EUR 17.69") - these are detail amounts
            if (/^EUR\s+[\d.,]+$/i.test(lineTextTrimmed)) return;

            // Skip timestamp lines (e.g. "25032026 21:19:30 K5361234")
            if (/^\d{8}\s+\d{2}:\d{2}:\d{2}/.test(lineTextTrimmed)) return;

            // Skip card/user number lines
            if (/^\d{10,}$/.test(lineTextTrimmed)) return;

            // Skip lines that are just dots
            if (/^\.*$/.test(lineTextTrimmed)) return;

            // Skip empty or very short lines
            if (lineTextTrimmed.length < 3) return;

            // Add useful description text
            const extra = lineTextTrimmed
                .replace(/\s+/g, ' ')
                .trim();

            if (extra && extra.length > 2) {
                currentTransaction.description += ' ' + extra;
            }
        }
    });

    // Push last transaction
    if (currentTransaction && (currentTransaction.debit > 0 || currentTransaction.credit > 0)) {
        transactions.push(currentTransaction);
    }

    // Clean descriptions
    transactions.forEach(t => {
        t.description = t.description.replace(/\s+/g, ' ').trim();
    });

    console.log('[BEK] Total transactions found:', transactions.length);
    return transactions;
};

const bek = {
    label: 'BEK',
    fullLabel: 'Banka Ekonomike',
    parseExcel: parseGenericBankExcel,
    parsePDF: parseBEKPDF
};

// ============================================================
// Ziraat Bank Kosova Parser
// ============================================================
// Date: DD.MM.YYYY HH:MM:SS (e.g. 31.01.2026 18:00:01)
// Header: Nr. Rendor | Data | Përfituesi | Përshkrimi i transaksionit | Qëllimi i pagesës | Numri i transaksionit | Debi | Kredi | Total
// EUROPEAN number format: comma as decimal, dot as thousands
// Separate Debi and Kredi columns with EUR suffix (e.g. "1,00 EUR", "10.000,00 EUR")
// Reference: Numri i transaksionit (e.g. 99900044583001)
// Multi-line: descriptions and beneficiary names can wrap

const ZIRAAT_SKIP_PATTERNS = [
    /ziraat\s+bank/i,
    /ukshin\s+hoti/i,
    /info@ziraat/i,
    /tczbxkpraxxx/i,
    /www\.ziraat/i,
    /bilanci i hapjes/i,
    /bilanci i\s*mbylljes/i,
    /qarkullimi\s+ditor/i,
    /qarkullimi\s+n[eë]\s+periudh/i,
    /numri i urdh[eë]res[eë]s/i,
    /numri i llogarise/i,
    /numri i llogarisë/i,
    /numri am[eë]/i,
    /^nr\.\s*ren/i,
    /^dor i\s/i,
    /^urdh/i,
    /^[eë]rit$/i,
    /p[eë]rshkrimi i\s*transaksionit/i,
    /q[eë]llimi i pages/i,
    /numri i transaksionit/i,
    /^\s*debi\s+kredi\s*$/i,
    /^\s*debi\s+kredi\s+total\s*$/i,
    /faqja\s+\d+\s*\/\s*\d+/i,
    /^\d{2}\.\d{2}\.\d{4}\s+\d{2}:\d{2}$/,  // standalone date-time footer
    /fsdk/i,
    /fondit p[eë]r sigurimin/i,
    /limiti i sigurimit/i,
    /ziraat banka [eë]sht[eë]/i,
    /deposit insurance/i,
    /coverage limit/i,
    /^\s*$/,
    /^nga\s+\d{2}\.\d{2}\.\d{4}/i,
    /^international maarif/i,
    /^adresa:/i,
    /797,78\s+EUR\s+1\.488/,  // summary row
];

const parseZiraatPDF = (pages) => {
    const transactions = [];
    const allLines = buildLines(pages, 4);

    console.log('[ZIRAAT] Total lines extracted:', allLines.length);

    // Find column X positions from header
    let debitColX = -1, creditColX = -1, totalColX = -1;
    for (const line of allLines) {
        const lower = line.text.toLowerCase();
        if (lower.includes('debi') && lower.includes('kredi') && lower.includes('total')) {
            line.items.forEach(item => {
                const t = item.text.toLowerCase().trim();
                if (t === 'debi') debitColX = item.x;
                if (t === 'kredi') creditColX = item.x;
                if (t === 'total') totalColX = item.x;
            });
            if (debitColX > 0) break;
        }
    }
    console.log(`[ZIRAAT] Columns - Debi: ${debitColX}, Kredi: ${creditColX}, Total: ${totalColX}`);

    let currentTransaction = null;

    allLines.forEach((line) => {
        const lineText = line.text;

        // Skip known patterns
        if (ZIRAAT_SKIP_PATTERNS.some(p => p.test(lineText))) return;

        // Ziraat transaction lines start with a row number followed by a date
        // Pattern: number + DD.MM.YYYY HH:MM:SS
        // Or check if line has a date with time
        const dateTimeMatch = lineText.match(/(\d{2}\.\d{2}\.\d{4})\s+(\d{2}:\d{2}:\d{2})/);

        if (dateTimeMatch) {
            // Verify this isn't a header/footer line
            const lower = lineText.toLowerCase();
            if (lower.includes('faqja') || lower.includes('bilanci') || lower.includes('qarkullimi')) return;

            // Save previous
            if (currentTransaction && (currentTransaction.debit > 0 || currentTransaction.credit > 0)) {
                transactions.push(currentTransaction);
            }

            const date = parseDate(dateTimeMatch[1]);
            if (!date) { currentTransaction = null; return; }

            const descParts = [];
            const amountsWithPos = [];
            let reference = '';

            line.items.forEach(item => {
                const val = item.text.trim();
                if (!val) return;

                // Skip date tokens
                if (/^\d{2}\.\d{2}\.\d{4}$/.test(val)) return;
                // Skip time tokens
                if (/^\d{2}:\d{2}:\d{2}$/.test(val)) return;
                // Skip row number (small number at far left)
                if (/^\d{1,3}$/.test(val) && item.x < 50) return;
                // Skip EUR suffix
                if (/^EUR$/i.test(val)) return;

                // Check for European amount (with optional EUR suffix)
                const cleanVal = val.replace(/\s*EUR\s*/gi, '').trim();
                if (/^-?\d{1,3}(?:\.\d{3})*,\d{2}$/.test(cleanVal)) {
                    const isNeg = cleanVal.startsWith('-');
                    const parsed = parseEuropeanAmount(cleanVal);
                    amountsWithPos.push({ x: item.x, val: parsed, raw: val, neg: isNeg });
                } else if (/^\d{11,}$/.test(val)) {
                    // Reference: Numri i transaksionit (long number like 99900044583001)
                    reference = val;
                } else {
                    descParts.push(val);
                }
            });

            const description = descParts.join(' ').replace(/\s+/g, ' ').trim();
            amountsWithPos.sort((a, b) => a.x - b.x);

            // Ziraat has separate Debi and Kredi columns + Total (balance)
            // Assign amounts based on column positions
            let debit = 0, credit = 0;
            const nonZero = amountsWithPos.filter(a => a.val > 0);

            if (debitColX > 0 && creditColX > 0 && totalColX > 0) {
                nonZero.forEach(a => {
                    const distDebit = Math.abs(a.x - debitColX);
                    const distCredit = Math.abs(a.x - creditColX);
                    const distTotal = Math.abs(a.x - totalColX);

                    // Skip Total (balance) column
                    if (distTotal < distDebit && distTotal < distCredit) return;

                    if (distDebit <= distCredit) {
                        debit = a.val;
                    } else {
                        credit = a.val;
                    }
                });
            } else if (debitColX > 0 && creditColX > 0) {
                nonZero.forEach(a => {
                    const distDebit = Math.abs(a.x - debitColX);
                    const distCredit = Math.abs(a.x - creditColX);
                    if (distDebit <= distCredit) debit = a.val;
                    else credit = a.val;
                });
            } else {
                // Fallback: if only one amount, use description hints
                if (nonZero.length >= 1) {
                    const upperDesc = description.toUpperCase();
                    if (upperDesc.includes('TRANSFER I NGA') || upperDesc.includes('INKASIM')) {
                        credit = nonZero[0].val;
                    } else {
                        debit = nonZero[0].val;
                    }
                }
            }

            currentTransaction = { date, description, reference, debit, credit };

        } else if (currentTransaction) {
            // Continuation line
            const lineTextTrimmed = lineText.trim();

            // Skip totals row
            if (/^\d{1,3}(?:\.\d{3})*,\d{2}\s+\d{1,3}(?:\.\d{3})*,\d{2}$/.test(lineTextTrimmed)) return;
            if (lineTextTrimmed.length < 3) return;

            // Check for reference in continuation
            const items = line.items || [];
            items.forEach(item => {
                const val = item.text.trim();
                if (/^\d{11,}$/.test(val) && !currentTransaction.reference) {
                    currentTransaction.reference = val;
                }
            });

            // Add description text (skip amounts, EUR, references)
            const extra = items
                .map(it => it.text.trim())
                .filter(t => t && t.length > 2 &&
                    !/^\d{2}\.\d{2}\.\d{4}$/.test(t) &&
                    !/^\d{2}:\d{2}:\d{2}$/.test(t) &&
                    !/^EUR$/i.test(t) &&
                    !/^\d{11,}$/.test(t) &&
                    !/^\d{1,3}$/.test(t) &&
                    !/^-?\d{1,3}(?:\.\d{3})*,\d{2}$/.test(t.replace(/\s*EUR\s*/gi, '').trim()))
                .join(' ').trim();

            if (extra && extra.length > 2) {
                currentTransaction.description += ' ' + extra;
            }
        }
    });

    // Push last
    if (currentTransaction && (currentTransaction.debit > 0 || currentTransaction.credit > 0)) {
        transactions.push(currentTransaction);
    }

    transactions.forEach(t => {
        t.description = t.description.replace(/\s+/g, ' ').trim();
    });

    console.log('[ZIRAAT] Total transactions found:', transactions.length);
    return transactions;
};

const ziraat = {
    label: 'Ziraat',
    fullLabel: 'Ziraat Bank Kosova',
    parseExcel: parseGenericBankExcel,
    parsePDF: parseZiraatPDF
};

// ============================================================
// ProCredit Bank Parser
// ============================================================
// Date: DD.MM.YYYY (two dates: Date + Value Date)
// Header: Date | Value Date | Id | Description | Reason | Debit | Credit | balance
// STANDARD number format: dot decimal, comma thousands (17,000.00 / 190.00 / 0.00)
// Separate Debit and Credit columns (0.00 = empty)
// Reference: Id column (e.g. 730467146)
// Multi-line: Reason text wraps across lines

const PROCREDIT_SKIP_PATTERNS = [
    /procredit\s+bank/i,
    /account\s+statement/i,
    /general\s+details/i,
    /transaction\s+details/i,
    /^iban/i,
    /contract\s+no/i,
    /^name:/i,
    /commercial\s+name/i,
    /ovd\s+limit/i,
    /previous\s+balance/i,
    /borxhi\/debts/i,
    /^total$/i,
    /fsdk/i,
    /deposit insurance/i,
    /coverage limit/i,
    /procredit bank [eë]sht[eë]/i,
    /^\d+\s+\d+$/,  // page numbers like "1 10"
    /^\d{2}\.\d{2}\.\d{4}\s+\d{2}:\d{2}:\d{2}\s+\w+\.\w+$/,  // footer timestamp
    /^\s*$/,
    /^date\s+value\s+date\s+id/i,
    /^date\s+value date/i,
];

const parseProCreditPDF = (pages) => {
    const transactions = [];
    const allLines = buildLines(pages, 3);

    console.log('[PROCREDIT] Total lines extracted:', allLines.length);

    // Find column positions from header
    let debitColX = -1, creditColX = -1, balanceColX = -1, idColX = -1;
    for (const line of allLines) {
        const lower = line.text.toLowerCase();
        if (lower.includes('debit') && lower.includes('credit') && lower.includes('balance')) {
            line.items.forEach(item => {
                const t = item.text.toLowerCase().trim();
                if (t === 'debit') debitColX = item.x;
                if (t === 'credit') creditColX = item.x;
                if (t === 'balance') balanceColX = item.x;
                if (t === 'id') idColX = item.x;
            });
            if (debitColX > 0) break;
        }
    }
    console.log(`[PROCREDIT] Columns - Debit: ${debitColX}, Credit: ${creditColX}, Balance: ${balanceColX}, Id: ${idColX}`);

    let currentTransaction = null;

    allLines.forEach((line) => {
        const lineText = line.text;

        // Skip known patterns
        if (PROCREDIT_SKIP_PATTERNS.some(p => p.test(lineText))) return;

        // ProCredit transaction lines start with DD.MM.YYYY DD.MM.YYYY
        const twoDateMatch = lineText.match(/^(\d{2}\.\d{2}\.\d{4})\s+(\d{2}\.\d{2}\.\d{4})\s+(.+)/);

        if (twoDateMatch) {
            // Skip header lines
            const lower = lineText.toLowerCase();
            if (lower.includes('account statement') || lower.includes('previous balance')) return;

            // Save previous
            if (currentTransaction && (currentTransaction.debit > 0 || currentTransaction.credit > 0)) {
                transactions.push(currentTransaction);
            }

            const date = parseDate(twoDateMatch[1]);
            if (!date) { currentTransaction = null; return; }

            const descParts = [];
            const amountsWithPos = [];
            let reference = '';

            line.items.forEach(item => {
                const val = item.text.trim();
                if (!val) return;

                // Skip date tokens
                if (/^\d{2}\.\d{2}\.\d{4}$/.test(val)) return;

                // Check if standard amount (with comma thousands, dot decimal)
                const cleaned = val.replace(/[€$£\s]/g, '');
                if (STANDARD_AMOUNT_REGEX.test(cleaned)) {
                    amountsWithPos.push({ x: item.x, val: parseAmount(val), raw: val, neg: cleaned.startsWith('-') });
                } else if (/^\d{6,12}$/.test(val)) {
                    // Id/Reference: 6-12 digit number (e.g. 730467146)
                    // Check if near Id column position
                    if (idColX > 0 && Math.abs(item.x - idColX) < 60) {
                        reference = val;
                    } else if (!reference) {
                        reference = val;
                    }
                } else {
                    descParts.push(val);
                }
            });

            const description = descParts.join(' ').replace(/\s+/g, ' ').trim();
            amountsWithPos.sort((a, b) => a.x - b.x);

            // Assign debit/credit using column positions, skip balance
            let debit = 0, credit = 0;
            const nonZero = amountsWithPos.filter(a => a.val > 0);

            if (debitColX > 0 && creditColX > 0) {
                nonZero.forEach(a => {
                    const distDebit = Math.abs(a.x - debitColX);
                    const distCredit = Math.abs(a.x - creditColX);
                    const distBalance = balanceColX > 0 ? Math.abs(a.x - balanceColX) : 9999;

                    // Skip balance column
                    if (distBalance < distDebit && distBalance < distCredit) return;

                    if (distDebit <= distCredit) {
                        debit = a.val;
                    } else {
                        credit = a.val;
                    }
                });
            } else {
                // Fallback
                if (nonZero.length >= 2) {
                    debit = nonZero[0].val;
                    credit = nonZero[1].val;
                } else if (nonZero.length === 1) {
                    debit = nonZero[0].val;
                }
            }

            currentTransaction = { date, description, reference, debit, credit };

        } else if (currentTransaction) {
            // Continuation line - append to description (Reason text wraps)
            const lineTextTrimmed = lineText.trim();

            // Skip page footer lines
            if (/^\d+\s+\d+$/.test(lineTextTrimmed)) return;
            if (/^\d{2}\.\d{2}\.\d{4}\s+\d{2}:\d{2}:\d{2}/.test(lineTextTrimmed)) return;
            if (lineTextTrimmed.length < 3) return;

            // Skip totals row (just amounts)
            if (/^\d{1,3}(?:,\d{3})*\.\d{2}\s+\d{1,3}(?:,\d{3})*\.\d{2}/.test(lineTextTrimmed) &&
                !lineTextTrimmed.match(/[a-zA-Z]/)) return;

            // Check for reference in continuation if not yet found
            if (!currentTransaction.reference) {
                line.items.forEach(item => {
                    const val = item.text.trim();
                    if (/^\d{6,12}$/.test(val) && idColX > 0 && Math.abs(item.x - idColX) < 60) {
                        currentTransaction.reference = val;
                    }
                });
            }

            // Add description text (filter out amounts and references)
            const extra = line.items
                .map(it => it.text.trim())
                .filter(t => t && t.length > 2 &&
                    !/^\d{2}\.\d{2}\.\d{4}$/.test(t) &&
                    !STANDARD_AMOUNT_REGEX.test(t.replace(/[€$£\s]/g, '')) &&
                    !/^\d{6,12}$/.test(t))
                .join(' ').trim();

            if (extra && extra.length > 2) {
                currentTransaction.description += ' ' + extra;
            }
        }
    });

    // Push last
    if (currentTransaction && (currentTransaction.debit > 0 || currentTransaction.credit > 0)) {
        transactions.push(currentTransaction);
    }

    transactions.forEach(t => {
        t.description = t.description.replace(/\s+/g, ' ').trim();
    });

    console.log('[PROCREDIT] Total transactions found:', transactions.length);
    return transactions;
};

const procredit = {
    label: 'ProCredit Bank',
    parseExcel: parseGenericBankExcel,
    parsePDF: parseProCreditPDF
};

// ============================================================
// Paysera Parser
// ============================================================
// Date: YYYY-MM-DD HH:MM:SS +TTTT (e.g. 2024-12-02 12:56:20 +0100)
// Types: "Transfer", "Taksa e provizionit"
// Columns: Lloji/Data | Nr. gjendjes/transferit | Përfitues/Pagues | EVP/IBAN | Shuma+valuta/Bilanci
// Amount format: standard dot decimal with sign (5000.00 EUR, -3.00 EUR)
// Negative = debit, Positive = credit
// Description on next line: "Qëllimi i pagesës: ..."
// Bilanci (balance) on same line or next sub-line

const PAYSERA_SKIP_PATTERNS = [
    /^paysera/i,
    /^gjendja e llogarise/i,
    /^gjendja e llogarisë/i,
    /^klienti\b/i,
    /^kodi i klientit/i,
    /^adresa e klientit/i,
    /^llogaria\b/i,
    /^valutat\b/i,
    /^bilanci n[eë] fillim/i,
    /^bilanci p[eë]rfundimtar/i,
    /^qarkullimi i debitit/i,
    /^qarkullimi i kredis/i,
    /^n[eë]nshkruar/i,
    /^lloji\s+data dhe koha/i,
    /^nr\.\s*i\s+gjendjes/i,
    /^nr\.\s*i\s+transferit/i,
    /^p[eë]rfitues.*pagues/i,
    /^evp\s*\/\s*iban/i,
    /^shuma dhe\s+valuta/i,
    /^\s*$/,
];

const parsePayseraPDF = (pages) => {
    const transactions = [];
    const allLines = buildLines(pages, 3);

    console.log('[PAYSERA] Total lines extracted:', allLines.length);

    let currentTransaction = null;

    // Paysera date: YYYY-MM-DD
    const PAYSERA_DATE_REGEX = /(\d{4}-\d{2}-\d{2})/;
    // Paysera time: HH:MM:SS +TTTT
    const PAYSERA_TIME_REGEX = /\d{2}:\d{2}:\d{2}\s*[+-]\d{4}/;
    // Purpose line
    const PAYSERA_PURPOSE_REGEX = /Q[eë]llimi i pages[eë]s:\s*(.*)/i;

    allLines.forEach((line) => {
        const lineText = line.text;

        // Skip known patterns
        if (PAYSERA_SKIP_PATTERNS.some(p => p.test(lineText))) return;

        // Check for purpose/description line
        const purposeMatch = lineText.match(PAYSERA_PURPOSE_REGEX);
        if (purposeMatch && currentTransaction) {
            const purpose = purposeMatch[1].trim();
            if (purpose) {
                currentTransaction.description = currentTransaction.description
                    ? currentTransaction.description + ' ' + purpose
                    : purpose;
            }
            return;
        }

        // Check for transaction line: has a date and either type keyword or amounts
        const dateMatch = lineText.match(PAYSERA_DATE_REGEX);
        const hasTime = PAYSERA_TIME_REGEX.test(lineText);

        if (dateMatch && hasTime) {
            // Skip header/summary lines that contain dates
            const lower = lineText.toLowerCase();
            if (lower.includes('bilanci') && lower.includes('fillim')) return;
            if (lower.includes('gjendja e llogarise') || lower.includes('gjendja e llogarisë')) return;
            if (lower.includes('nënshkruar') || lower.includes('nenshkruar')) return;

            // Save previous
            if (currentTransaction && (currentTransaction.debit > 0 || currentTransaction.credit > 0)) {
                transactions.push(currentTransaction);
            }

            const date = parseDate(dateMatch[1]);
            if (!date) { currentTransaction = null; return; }

            const descParts = [];
            const amountsWithPos = [];
            let reference = '';

            line.items.forEach(item => {
                const val = item.text.trim();
                if (!val) return;

                // Skip date tokens (YYYY-MM-DD)
                if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return;
                // Skip time tokens (HH:MM:SS +TTTT)
                if (/^\d{2}:\d{2}:\d{2}$/.test(val)) return;
                if (/^[+-]\d{4}$/.test(val)) return;
                // Skip EUR
                if (/^EUR$/i.test(val)) return;

                // Check if amount: standard format with optional negative sign
                // Paysera amounts: 5000.00, -3.00, -5000.00, 47000.00, -45200.00
                const cleaned = val.replace(/\s*EUR\s*/gi, '').trim();
                if (/^-?\d{1,3}(?:,\d{3})*\.\d{2}$/.test(cleaned)) {
                    const isNeg = cleaned.startsWith('-');
                    const parsed = parseAmount(cleaned);
                    if (parsed > 0) {
                        amountsWithPos.push({ x: item.x, val: parsed, raw: val, neg: isNeg });
                    }
                } else if (/^\d{7,}$/.test(val)) {
                    // Reference numbers: Nr. i gjendjes or Nr. i transferit (7+ digit numbers)
                    if (!reference) reference = val;
                    else reference = reference + '/' + val;
                } else {
                    // Skip type keywords from description to keep it clean
                    if (/^Transfer$/i.test(val)) return;
                    if (/^Taksa$/i.test(val) || /^provizionit$/i.test(val)) return;
                    descParts.push(val);
                }
            });

            // Remove IBAN-like strings and codes from description, keep counterparty name
            const description = descParts
                .filter(p => !/^[A-Z]{2}\d{2}[A-Z0-9]{10,}$/.test(p)) // Remove IBANs
                .filter(p => !/^EVP\d+$/.test(p)) // Remove EVP accounts
                .filter(p => !/^\(\d+\)$/.test(p)) // Remove (code) patterns
                .filter(p => !/^\d{9,}$/.test(p)) // Remove long numbers
                .join(' ').replace(/\s+/g, ' ').trim();

            amountsWithPos.sort((a, b) => a.x - b.x);

            // Paysera: first amount = transaction amount, last amount = balance
            // Sign determines direction: negative = debit, positive = credit
            let debit = 0, credit = 0;

            if (amountsWithPos.length >= 2) {
                // First amount is the transaction, second is balance
                const txAmount = amountsWithPos[0];
                if (txAmount.neg) {
                    debit = txAmount.val;
                } else {
                    credit = txAmount.val;
                }
            } else if (amountsWithPos.length === 1) {
                const txAmount = amountsWithPos[0];
                if (txAmount.neg) {
                    debit = txAmount.val;
                } else {
                    credit = txAmount.val;
                }
            }

            currentTransaction = { date, description, reference, debit, credit };
        }
    });

    // Push last
    if (currentTransaction && (currentTransaction.debit > 0 || currentTransaction.credit > 0)) {
        transactions.push(currentTransaction);
    }

    transactions.forEach(t => { t.description = t.description.replace(/\s+/g, ' ').trim(); });

    console.log('[PAYSERA] Total transactions found:', transactions.length);
    return transactions;
};

const paysera = {
    label: 'Paysera',
    fullLabel: 'Paysera',
    parseExcel: parseGenericBankExcel,
    parsePDF: parsePayseraPDF
};

// ============================================================
// OneFor Parser
// ============================================================
// Date: YYYY-MM-DD (e.g. 2025-10-28)
// Header: Transaction Date | Counterparty | Description | Transaction Type | Transaction ID | Currency | Debit | Credit | Balance
// Amount format: standard (150,600.00, 3.00, 65,000.00)
// Separate Debit and Credit columns
// Reference: Transaction ID (e.g. 200973/30)

const ONEFOR_SKIP_PATTERNS = [
    /^account\s+statement/i,
    /^customer/i,
    /^account\s+number/i,
    /^statement\s+period/i,
    /^generated\s+on/i,
    /^total\s+credit/i,
    /^total\s+debit/i,
    /^start\s+balance/i,
    /^end\s+balance/i,
    /^available\s+balance/i,
    /^note:/i,
    /^transaction\s+date\s+counterparty/i,
    /^one\s*for$/i,
    /^\s*$/,
];

const parseOneForPDF = (pages) => {
    const transactions = [];
    const allLines = buildLines(pages, 3);

    console.log('[ONEFOR] Total lines extracted:', allLines.length);

    // Find column positions
    let debitColX = -1, creditColX = -1, balanceColX = -1;
    for (const line of allLines) {
        const lower = line.text.toLowerCase();
        if (lower.includes('debit') && lower.includes('credit') && lower.includes('balance')) {
            line.items.forEach(item => {
                const t = item.text.toLowerCase().trim();
                if (t === 'debit') debitColX = item.x;
                if (t === 'credit') creditColX = item.x;
                if (t === 'balance') balanceColX = item.x;
            });
            if (debitColX > 0) break;
        }
    }
    console.log(`[ONEFOR] Columns - Debit: ${debitColX}, Credit: ${creditColX}, Balance: ${balanceColX}`);

    let currentTransaction = null;

    allLines.forEach((line) => {
        const lineText = line.text;

        // Skip known patterns
        if (ONEFOR_SKIP_PATTERNS.some(p => p.test(lineText))) return;

        // OneFor date: YYYY-MM-DD at the start
        const dateMatch = lineText.match(/^(\d{4}-\d{2}-\d{2})\s/);

        if (dateMatch) {
            // Skip header/summary lines
            const lower = lineText.toLowerCase();
            if (lower.includes('statement period') || lower.includes('generated on')) return;

            // Save previous
            if (currentTransaction && (currentTransaction.debit > 0 || currentTransaction.credit > 0)) {
                transactions.push(currentTransaction);
            }

            const date = parseDate(dateMatch[1]);
            if (!date) { currentTransaction = null; return; }

            const descParts = [];
            const amountsWithPos = [];
            let reference = '';

            line.items.forEach(item => {
                const val = item.text.trim();
                if (!val) return;

                // Skip date token
                if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return;
                // Skip currency
                if (/^EUR$/i.test(val)) return;

                // Check for amount (standard format)
                const cleaned = val.replace(/[€$£\s]/g, '').replace(/,/g, '');
                if (/^\d+\.\d{2}$/.test(cleaned) && parseFloat(cleaned) >= 0) {
                    amountsWithPos.push({ x: item.x, val: parseAmount(val), raw: val, neg: val.startsWith('-') });
                } else if (/^\d{6}\/\d{1,3}$/.test(val)) {
                    // Transaction ID like 200973/30
                    reference = val;
                } else {
                    descParts.push(val);
                }
            });

            const description = descParts.join(' ').replace(/\s+/g, ' ').trim();
            amountsWithPos.sort((a, b) => a.x - b.x);

            // OneFor has separate Debit, Credit, and Balance columns
            let debit = 0, credit = 0;
            const nonZero = amountsWithPos.filter(a => a.val > 0);

            if (debitColX > 0 && creditColX > 0) {
                nonZero.forEach(a => {
                    const distDebit = Math.abs(a.x - debitColX);
                    const distCredit = Math.abs(a.x - creditColX);
                    const distBalance = balanceColX > 0 ? Math.abs(a.x - balanceColX) : 9999;

                    // Skip balance column
                    if (distBalance < distDebit && distBalance < distCredit) return;

                    if (distDebit <= distCredit) {
                        debit = a.val;
                    } else {
                        credit = a.val;
                    }
                });
            } else {
                // Fallback
                const { debit: d, credit: c } = assignAmounts(nonZero, debitColX, creditColX, balanceColX);
                debit = d;
                credit = c;
            }

            currentTransaction = { date, description, reference, debit, credit };

        } else if (currentTransaction) {
            // Continuation line
            const extra = line.items
                .map(it => it.text.trim())
                .filter(t => t && t.length > 2 &&
                    !/^\d{4}-\d{2}-\d{2}$/.test(t) &&
                    !/^EUR$/i.test(t) &&
                    !STANDARD_AMOUNT_REGEX.test(t.replace(/[€$£\s]/g, '')) &&
                    !/^\d{6}\/\d{1,3}$/.test(t))
                .join(' ').trim();
            if (extra) currentTransaction.description += ' ' + extra;
        }
    });

    // Push last
    if (currentTransaction && (currentTransaction.debit > 0 || currentTransaction.credit > 0)) {
        transactions.push(currentTransaction);
    }

    transactions.forEach(t => { t.description = t.description.replace(/\s+/g, ' ').trim(); });

    console.log('[ONEFOR] Total transactions found:', transactions.length);
    return transactions;
};

const onefor = {
    label: 'OneFor',
    fullLabel: 'OneFor',
    parseExcel: parseGenericBankExcel,
    parsePDF: parseOneForPDF
};

const generic = {
    label: 'Tjeter',
    fullLabel: 'Tjeter / Generic',
    parseExcel: parseGenericBankExcel,
    parsePDF: parseGenericBankPDF
};

export const BANK_PARSERS = { bkt, teb, nlb, bpb, rbko, bek, ziraat, procredit, paysera, onefor, generic };

export const BANK_OPTIONS = Object.entries(BANK_PARSERS).map(([key, val]) => ({
    value: key,
    label: val.label,
    fullLabel: val.fullLabel || val.label
}));

// ============================================================
// CSV Extraction
// ============================================================
const extractDataFromCSV = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const text = evt.target.result;
                // Auto-detect delimiter: semicolon, comma, or tab
                const firstLine = text.split('\n')[0] || '';
                let delimiter = ',';
                if (firstLine.split(';').length > firstLine.split(',').length) delimiter = ';';
                else if (firstLine.split('\t').length > firstLine.split(',').length) delimiter = '\t';

                const rows = [];
                const lines = text.split(/\r?\n/);
                for (const line of lines) {
                    if (!line.trim()) continue;
                    // Simple CSV parse respecting quoted fields
                    const cells = [];
                    let current = '';
                    let inQuotes = false;
                    for (let i = 0; i < line.length; i++) {
                        const ch = line[i];
                        if (ch === '"') {
                            if (inQuotes && line[i + 1] === '"') {
                                current += '"';
                                i++;
                            } else {
                                inQuotes = !inQuotes;
                            }
                        } else if (ch === delimiter && !inQuotes) {
                            cells.push(current.trim());
                            current = '';
                        } else {
                            current += ch;
                        }
                    }
                    cells.push(current.trim());
                    rows.push(cells);
                }
                resolve(rows);
            } catch (e) {
                reject(e);
            }
        };
        reader.onerror = reject;
        reader.readAsText(file, 'UTF-8');
    });
};

// ============================================================
// Main parse function
// ============================================================
export const parseBankStatement = async (file, bankKey) => {
    const parser = BANK_PARSERS[bankKey] || BANK_PARSERS.generic;
    const fileName = file.name.toLowerCase();
    const isPDF = fileName.endsWith('.pdf');
    const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
    const isCSV = fileName.endsWith('.csv');

    if (isPDF) {
        const pages = await extractTextFromPDF(file);
        return parser.parsePDF(pages);
    } else if (isExcel) {
        const data = await extractDataFromExcel(file);
        return parser.parseExcel(data);
    } else if (isCSV) {
        const data = await extractDataFromCSV(file);
        return parser.parseExcel(data); // CSV rows have same structure as Excel rows
    } else {
        throw new Error('Formati i skedarit nuk mbeshtetet. Perdorni Excel (.xlsx, .xls), CSV (.csv) ose PDF (.pdf)');
    }
};

export default parseBankStatement;
